import type { OccurrenceDto } from '@familynest/shared'
import { WEEKDAY_LABELS, isSameDay, isSameMonth, monthMatrix, formatTime } from '../../lib/date'
import { layoutWeek, occurrenceDayRange, type WeekSegment } from './utils'

// Vertikales Raster der Balken je Tageszelle.
const HEADER = 28 // px, Platz für die Datumszahl oben
const LANE_H = 22 // px pro Lane (Balkenhöhe inkl. Abstand)
const MIN_LANES = 3 // Mindesthöhe je Wochenzeile; mehr Termine lassen die Zeile wachsen
const ROW_PAD = 4 // px Puffer unten je Wochenzeile

/**
 * Monatsansicht — 6×7-Raster. Termine werden als horizontale Balken über alle
 * betroffenen Tage gezeichnet; mehrtägige Termine laufen so durchgehend über
 * mehrere Tage (10 – UI/UX). Es werden IMMER alle Termine angezeigt — die
 * Wochenzeile wächst mit der Anzahl der Balken, der Kalender scrollt bei Bedarf.
 */
export function MonthView({
  cursor,
  occurrences,
  colorFor,
  onDayClick,
  onEventClick,
}: {
  cursor: Date
  occurrences: OccurrenceDto[]
  colorFor: (occ: OccurrenceDto) => string
  onDayClick: (day: Date) => void
  onEventClick: (occ: OccurrenceDto) => void
}): React.JSX.Element {
  const weeks = monthMatrix(cursor)
  const today = new Date()
  // Abos „als Hintergrund" (Feiertage/Ferien, ADR-089) tönen die Tageszelle
  // statt einen Balken zu belegen.
  const bars = occurrences.filter((o) => !o.renderAsBackground)
  const backgrounds = occurrences.filter((o) => o.renderAsBackground)

  return (
    <div className="flex h-full flex-col">
      <div className="grid grid-cols-7">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="px-2 py-1 text-center text-xs font-medium" style={{ color: 'var(--color-muted)' }}>
            {w}
          </div>
        ))}
      </div>
      <div
        className="flex flex-1 flex-col overflow-y-auto rounded-[var(--radius-card)] border"
        style={{ borderColor: 'var(--color-line)' }}
      >
        {weeks.map((week, wi) => (
          <WeekRow
            key={wi}
            week={week}
            cursor={cursor}
            today={today}
            segments={layoutWeek(week, bars)}
            backgrounds={backgrounds}
            colorFor={colorFor}
            onDayClick={onDayClick}
            onEventClick={onEventClick}
          />
        ))}
      </div>
    </div>
  )
}

function WeekRow({
  week,
  cursor,
  today,
  segments,
  backgrounds,
  colorFor,
  onDayClick,
  onEventClick,
}: {
  week: Date[]
  cursor: Date
  today: Date
  segments: WeekSegment[]
  backgrounds: OccurrenceDto[]
  colorFor: (occ: OccurrenceDto) => string
  onDayClick: (day: Date) => void
  onEventClick: (occ: OccurrenceDto) => void
}): React.JSX.Element {
  // Alle Termine anzeigen: höchste belegte Lane bestimmt die Zeilenhöhe.
  const laneCount = segments.reduce((max, s) => Math.max(max, s.lane + 1), 0)
  const rowMinHeight = HEADER + Math.max(laneCount, MIN_LANES) * LANE_H + ROW_PAD

  return (
    <div className="relative grid flex-1 grid-cols-7" style={{ minHeight: rowMinHeight }}>
      {week.map((day) => {
        const inMonth = isSameMonth(day, cursor)
        const isToday = isSameDay(day, today)
        const t = day.getTime()
        const bg = backgrounds.filter((o) => {
          const r = occurrenceDayRange(o)
          return t >= r.startDay.getTime() && t <= r.endDay.getTime()
        })
        const tint = bg[0] ? `color-mix(in srgb, ${colorFor(bg[0])} 9%, var(--color-card))` : 'var(--color-card)'
        return (
          <button
            key={day.toISOString()}
            onClick={() => onDayClick(day)}
            className="flex min-h-24 flex-col items-start border-b border-r p-1 text-left transition-colors last:border-r-0 hover:brightness-95"
            style={{ backgroundColor: tint, borderColor: 'var(--color-line)', opacity: inMonth ? 1 : 0.45 }}
            title={bg.map((o) => o.title).join(' · ') || undefined}
          >
            <span className="flex w-full min-w-0 items-center gap-1">
              <span
                className="inline-flex size-6 shrink-0 items-center justify-center rounded-full text-xs"
                style={isToday ? { backgroundColor: 'var(--color-accent)', color: '#fff' } : { color: 'var(--color-ink)' }}
              >
                {day.getDate()}
              </span>
              {bg[0] && (
                <span className="truncate text-[10px]" style={{ color: 'var(--color-muted)' }}>
                  {bg.map((o) => o.title).join(' · ')}
                </span>
              )}
            </span>
          </button>
        )
      })}

      {/* Balken-Overlay: mehrtägige Termine laufen durchgehend über die Tage. */}
      <div className="pointer-events-none absolute inset-0">
        {segments.map((s) => {
            const color = colorFor(s.occ)
            return (
              <button
                key={s.occ.id + s.occ.occurrenceStart}
                onClick={(e) => {
                  e.stopPropagation()
                  onEventClick(s.occ)
                }}
                title={s.occ.title}
                className="pointer-events-auto absolute flex h-5 items-center gap-1 overflow-hidden px-1 text-xs"
                style={{
                  top: HEADER + s.lane * LANE_H,
                  left: `calc(${(s.colStart / 7) * 100}% + 2px)`,
                  width: `calc(${(s.colSpan / 7) * 100}% - 4px)`,
                  // Tönung theme-abhängig: hell zart, dunkel kräftig (--event-bar-tint),
                  // sonst ist der Balken im Dark-Mode kaum sichtbar.
                  backgroundColor: `color-mix(in srgb, ${color} var(--event-bar-tint, 13%), var(--color-card))`,
                  color: 'var(--color-ink)',
                  borderTopLeftRadius: s.continuesLeft ? 0 : 4,
                  borderBottomLeftRadius: s.continuesLeft ? 0 : 4,
                  borderTopRightRadius: s.continuesRight ? 0 : 4,
                  borderBottomRightRadius: s.continuesRight ? 0 : 4,
                  borderLeft: s.continuesLeft ? `2px solid ${color}` : undefined,
                }}
              >
                <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                {!s.occ.allDay && !s.continuesLeft && (
                  <span style={{ color: 'var(--color-muted)' }}>{formatTime(s.occ.occurrenceStart)}</span>
                )}
                <span className="truncate">{s.occ.title}</span>
              </button>
            )
          })}
      </div>
    </div>
  )
}
